import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api/config';
// import './CampGallery.css';

const CampGallery = () => {
  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const containerRef = useRef(null);
  const autoPlayRef = useRef(null);

  const API_URL = `${API_BASE_URL}/api/camp_highlights/read_isShown`;
  const IMAGE_PREFIX = 'https://ethiccraft.org/';

  // --- 1. Fetch Data ---
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(API_URL);
        // Ensure we handle the data structure correctly
        const arr = Array.isArray(response.data) ? response.data : (response.data.data || []);
        const fetchedImages = arr.map(img => ({
          url: IMAGE_PREFIX + img.image,
          id: img.id || Math.random() // Fallback ID
        }));
        setImages(fetchedImages);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    };
    fetchImages();
  }, []);

  // --- 2. Navigation Logic ---
  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToSlide = (index) => {
    setActiveIndex(index);
  };

  // --- 3. Keyboard Support ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  // --- 4. Touch & Mouse Drag Logic ---
  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const diff = startX - clientX;
    setDragOffset(diff); // Visual feedback during drag
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    const threshold = 50; // Minimum distance to trigger slide change
    
    if (dragOffset > threshold) {
      handleNext();
    } else if (dragOffset < -threshold) {
      handlePrev();
    }
    
    setIsDragging(false);
    setDragOffset(0);
  };

  // --- 5. Auto Play (Pauses on Hover) ---
  useEffect(() => {
    if (isDragging) return; // Don't autoplay while user is interacting
    
    autoPlayRef.current = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(autoPlayRef.current);
  }, [activeIndex, isDragging, handleNext]);


  if (images.length === 0) return null;

  return (
    <section className="premium-gallery-section">
      {/* Ambient Background Layer */}
      <div className="ambient-background">
        <div 
            className="ambient-blur"
            style={{ backgroundImage: `url(${images[activeIndex].url})` }}
        />
        <div className="ambient-overlay"></div>
      </div>

      <div className="gallery-content-wrapper">
        <div className="gallery-header">
            <span className="gallery-subtitle">CAPTURING MOMENTS</span>
            <h2 className="gallery-title">Memories from the Camps</h2>
            <div className="title-underline"></div>
        </div>

        {/* The Carousel Track */}
        <div 
            className="carousel-container"
            ref={containerRef}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
        >
            <div className="carousel-track">
                {images.map((image, index) => {
                    // Calculate relative position for 3D effect
                    let position = "inactive";
                    if (index === activeIndex) position = "active";
                    else if (index === activeIndex - 1 || (activeIndex === 0 && index === images.length - 1)) position = "prev";
                    else if (index === activeIndex + 1 || (activeIndex === images.length - 1 && index === 0)) position = "next";

                    return (
                        <div 
                            key={index} 
                            className={`carousel-card ${position}`}
                            onClick={() => goToSlide(index)}
                        >
                            <img src={image.url} alt="Camp Memory" />
                            <div className="card-shine"></div>
                        </div>
                    );
                })}
            </div>
        </div>

        {/* Navigation Controls */}
        <div className="gallery-controls">
            <button className="nav-btn prev" onClick={handlePrev} aria-label="Previous Image">
                &#8592;
            </button>
            
            <div className="progress-indicators">
                {images.map((_, idx) => (
                    <div 
                        key={idx} 
                        className={`indicator ${idx === activeIndex ? 'active' : ''}`}
                        onClick={() => goToSlide(idx)}
                    />
                ))}
            </div>

            <button className="nav-btn next" onClick={handleNext} aria-label="Next Image">
                &#8594;
            </button>
        </div>
      </div>
    </section>
  );
};

export default CampGallery;